import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { FeeRangeLine } from "../../utils/feeFormat";

export interface FeeRangeRulerProps {
  lines: FeeRangeLine[];
  /** Index of the band the example operation falls into. */
  activeIndex: number | null;
  className?: string;
}

/**
 * The amount axis of ONE operation cut into its bands, left to right. Bands get equal widths on purpose: real
 * ranges span orders of magnitude, and a proportional ruler would squeeze the first ones into slivers.
 */
export function FeeRangeRuler({ lines, activeIndex, className }: FeeRangeRulerProps) {
  const { t } = useTranslations("paymentFees");

  return (
    <ol aria-label={t("tiers_ruler_aria")} className={cn("flex w-full gap-1", className)}>
      {lines.map((line, index) => {
        const active = index === activeIndex;
        return (
          <li
            key={`${line.label}-${index}`}
            aria-current={active ? "true" : undefined}
            className={cn(
              "flex min-w-0 flex-1 flex-col gap-1 border-t-4 pt-1.5 transition-colors",
              active ? "border-primary" : line.isBase ? "border-border" : "border-primary/30",
            )}
          >
            <span
              className={cn(
                "truncate text-[11px] tabular-nums",
                active ? "font-semibold text-foreground" : "text-muted-foreground",
              )}
              title={line.label}
            >
              {line.label}
            </span>
            <span
              className={cn(
                "truncate font-mono text-xs tabular-nums",
                active ? "font-semibold text-primary" : "text-foreground",
              )}
              title={line.price}
            >
              {line.price}
              {line.isBase && (
                <span className="ml-1 font-sans text-muted-foreground">· {t("range_base")}</span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
